// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as ProjectionIntegration from './configuration.projectionIntegration';
import * as Core from './configuration.core';
import * as ProjectionBasic from './configuration.projectionBasic';
import * as ProjectionLayout from './configuration.projectionLayout';
import * as ModelLayout from './configuration.modelLayout';
import * as ModelDictionary from './configuration.modelDictionary';
import * as ProjectionLayoutGrid from './configuration.projectionLayoutGrid';
import * as ModelGrid from './configuration.modelGrid';

export function isConflictScenarioLayoutModel(source: any): source is IConflictScenarioLayout {
    // Method Implementation: simple
    return source.modelName === 'ConflictScenarioLayout';
}

export function isConflictScenarioLayoutQuestionModel(source: any): source is IConflictScenarioLayoutQuestion {
    // Method Implementation: simple
    return source.modelName === 'ConflictScenarioLayoutQuestion';
}

export interface ConflictScenarioLayoutZodSchemaShape {
    questions?: ConflictScenarioLayoutQuestionZodSchemaShape[];
}

export interface ConflictScenarioLayoutQuestionZodSchemaShape {
    source?: string;

    displayName?: string;

    oldValue?: string;

    newValue?: string;
}

// Artifact Implementation: artifacts/zodSchemaType
export const ConflictScenarioLayoutZodSchema: Zod.ZodSchema<ConflictScenarioLayoutZodSchemaShape> = Zod.lazy(() => Zod.object({
    questions: Zod.array(ConflictScenarioLayoutQuestionZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConflictScenarioLayoutQuestionZodSchema: Zod.ZodSchema<ConflictScenarioLayoutQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    source: Zod.string().optional(),
    displayName: Zod.string().optional(),
    oldValue: Zod.string().optional(),
    newValue: Zod.string().optional()
}).nonstrict());

export interface IConflictScenarioLayout extends Wellspring.IModel {
    readonly modelName: string;

    readonly questions: ConflictScenarioLayoutQuestion[];

    readonly isQuestionsDefined: boolean;
}

export interface IConflictScenarioLayoutQuestion extends Wellspring.IModel {
    readonly modelName: string;

    readonly source: string;

    readonly displayName: string;

    readonly oldValue: string;

    readonly newValue: string;

    readonly isSourceDefined: boolean;

    readonly isDisplayNameDefined: boolean;

    readonly isOldValueDefined: boolean;

    readonly isNewValueDefined: boolean;
}

export class ConflictScenarioLayout implements IConflictScenarioLayout {
    public static readonly modelName: string = 'ConflictScenarioLayout';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'questions' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConflictScenarioLayout.modelName;
    }

    private _questions: ConflictScenarioLayoutQuestion[];

    public get questions(): ConflictScenarioLayoutQuestion[] {
        // Method Implementation: simple
        return this._questions;
    }

    public set questions(value: ConflictScenarioLayoutQuestion[]) {
        // Method Implementation: simple
        this._questions = value;
        this._isQuestionsDefined = true;
    }

    private _isQuestionsDefined: boolean;

    public get isQuestionsDefined(): boolean {
        // Method Implementation: simple
        return this._isQuestionsDefined;
    }

    public constructor();
    public constructor(source: IConflictScenarioLayout);
    public constructor(source: Partial<Pick<ConflictScenarioLayout, 'questions'>>);
    public constructor(source?: IConflictScenarioLayout | Partial<Pick<ConflictScenarioLayout, 'questions'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConflictScenarioLayoutModel(source)) {
                if (source.isQuestionsDefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
            }
            else {
                if (source.questions !== undefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
            }
        }
        else {
            this._questions = [];
            this._isQuestionsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConflictScenarioLayout.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isQuestionsDefined && this.questions !== undefined)
            definedProperties.push('questions');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'questions':
                return this.questions;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isQuestionsDefined && this.questions !== undefined) {
            if (this.questions !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.questions.length));
                for (let i = 0; i < this.questions.length; i++) {
                    const item = this.questions[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'questions':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.questions = [];
                
                    let item: ConflictScenarioLayoutQuestion = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ConflictScenarioLayoutQuestion.modelName) {
                                item = new ConflictScenarioLayoutQuestion();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConflictScenarioLayoutQuestion.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'questions' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'questions'`);
                        }
                
                        this.questions.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'questions' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'questions'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // questions
        if (this.isQuestionsDefined && this.questions !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.questions === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.questions.length);
            
                for (const item of this.questions) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // questions
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ConflictScenarioLayoutQuestion;
            
                    this.questions = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ConflictScenarioLayoutQuestion();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.questions.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConflictScenarioLayout {
        // Method Implementation: simple
        const schema = ConflictScenarioLayoutZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConflictScenarioLayoutZodSchemaShape): ConflictScenarioLayout {
        // Method Implementation: parseZodSchema
        return new ConflictScenarioLayout({
            questions: schema.questions?.map(i => ConflictScenarioLayoutQuestion.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questions: this.isQuestionsDefined ? (this.questions?.map(item => item)) : undefined,
        };
    }

    public clone(): ConflictScenarioLayout {
        // Method Implementation: clone
        return new ConflictScenarioLayout({
            questions: this.isQuestionsDefined ? this.questions?.map(i => i.clone()) : undefined
        });
    }
}

export class ConflictScenarioLayoutQuestion implements IConflictScenarioLayoutQuestion {
    public static readonly modelName: string = 'ConflictScenarioLayoutQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'source', 'displayName', 'oldValue', 'newValue' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConflictScenarioLayoutQuestion.modelName;
    }

    private _source: string;

    private _displayName: string;

    private _oldValue: string;

    private _newValue: string;

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get displayName(): string {
        // Method Implementation: simple
        return this._displayName;
    }

    public get oldValue(): string {
        // Method Implementation: simple
        return this._oldValue;
    }

    public get newValue(): string {
        // Method Implementation: simple
        return this._newValue;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set displayName(value: string) {
        // Method Implementation: simple
        this._displayName = value;
        this._isDisplayNameDefined = true;
    }

    public set oldValue(value: string) {
        // Method Implementation: simple
        this._oldValue = value;
        this._isOldValueDefined = true;
    }

    public set newValue(value: string) {
        // Method Implementation: simple
        this._newValue = value;
        this._isNewValueDefined = true;
    }

    private _isSourceDefined: boolean;

    private _isDisplayNameDefined: boolean;

    private _isOldValueDefined: boolean;

    private _isNewValueDefined: boolean;

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isDisplayNameDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayNameDefined;
    }

    public get isOldValueDefined(): boolean {
        // Method Implementation: simple
        return this._isOldValueDefined;
    }

    public get isNewValueDefined(): boolean {
        // Method Implementation: simple
        return this._isNewValueDefined;
    }

    public constructor();
    public constructor(source: IConflictScenarioLayoutQuestion);
    public constructor(source: Partial<Pick<ConflictScenarioLayoutQuestion, 'source' | 'displayName' | 'oldValue' | 'newValue'>>);
    public constructor(source?: IConflictScenarioLayoutQuestion | Partial<Pick<ConflictScenarioLayoutQuestion, 'source' | 'displayName' | 'oldValue' | 'newValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConflictScenarioLayoutQuestionModel(source)) {
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isDisplayNameDefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.isOldValueDefined) {
                    this._oldValue = source.oldValue;
                    this._isOldValueDefined = true;
                }
                else {
                    this._oldValue = '';
                    this._isOldValueDefined = false;
                }
                
                if (source.isNewValueDefined) {
                    this._newValue = source.newValue;
                    this._isNewValueDefined = true;
                }
                else {
                    this._newValue = '';
                    this._isNewValueDefined = false;
                }
            }
            else {
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.displayName !== undefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.oldValue !== undefined) {
                    this._oldValue = source.oldValue;
                    this._isOldValueDefined = true;
                }
                else {
                    this._oldValue = '';
                    this._isOldValueDefined = false;
                }
                
                if (source.newValue !== undefined) {
                    this._newValue = source.newValue;
                    this._isNewValueDefined = true;
                }
                else {
                    this._newValue = '';
                    this._isNewValueDefined = false;
                }
            }
        }
        else {
            this._source = '';
            this._isSourceDefined = false;
        
            this._displayName = '';
            this._isDisplayNameDefined = false;
        
            this._oldValue = '';
            this._isOldValueDefined = false;
        
            this._newValue = '';
            this._isNewValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConflictScenarioLayoutQuestion.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isDisplayNameDefined && this.displayName !== undefined)
            definedProperties.push('displayName');
        if (this.isOldValueDefined && this.oldValue !== undefined)
            definedProperties.push('oldValue');
        if (this.isNewValueDefined && this.newValue !== undefined)
            definedProperties.push('newValue');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'source':
                return this.source;
            case 'displayName':
                return this.displayName;
            case 'oldValue':
                return this.oldValue;
            case 'newValue':
                return this.newValue;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            if (this.displayName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'null'));
            }
        }
        
        if (this.isOldValueDefined && this.oldValue !== undefined) {
            if (this.oldValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('oldValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.oldValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('oldValue', 'null'));
            }
        }
        
        if (this.isNewValueDefined && this.newValue !== undefined) {
            if (this.newValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('newValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.newValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('newValue', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'displayName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayName' cannot be null`);
                    this.displayName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayName'`);
                }
        
                return true;
            case 'oldValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.oldValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'oldValue' cannot be null`);
                    this.oldValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'oldValue'`);
                }
        
                return true;
            case 'newValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.newValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'newValue' cannot be null`);
                    this.newValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'newValue'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayName
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // oldValue
        if (this.isOldValueDefined && this.oldValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.oldValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.oldValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // newValue
        if (this.isNewValueDefined && this.newValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.newValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.newValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayName = <any>null;
                    break;
                case 1:
                    this.displayName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // oldValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.oldValue = <any>null;
                    break;
                case 1:
                    this.oldValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // newValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.newValue = <any>null;
                    break;
                case 1:
                    this.newValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConflictScenarioLayoutQuestion {
        // Method Implementation: simple
        const schema = ConflictScenarioLayoutQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConflictScenarioLayoutQuestionZodSchemaShape): ConflictScenarioLayoutQuestion {
        // Method Implementation: parseZodSchema
        return new ConflictScenarioLayoutQuestion({
            source: schema.source,
            displayName: schema.displayName,
            oldValue: schema.oldValue,
            newValue: schema.newValue
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            displayName: this.isDisplayNameDefined ? ((this.displayName === null ? undefined : this.displayName)) : undefined,
            oldValue: this.isOldValueDefined ? ((this.oldValue === null ? undefined : this.oldValue)) : undefined,
            newValue: this.isNewValueDefined ? ((this.newValue === null ? undefined : this.newValue)) : undefined,
        };
    }

    public clone(): ConflictScenarioLayoutQuestion {
        // Method Implementation: clone
        return new ConflictScenarioLayoutQuestion({
            source: this.isSourceDefined ? this.source : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            oldValue: this.isOldValueDefined ? this.oldValue : undefined,
            newValue: this.isNewValueDefined ? this.newValue : undefined
        });
    }
}

export interface IProviderService {
    createIntegration(coreEntity: Core.Entity): Promise<ProjectionIntegration.Entity>;

    createBasic(coreEntity: Core.Entity): Promise<ProjectionBasic.Entity>;

    createLayout(coreEntity: Core.Entity): Promise<ProjectionLayout.Entity>;

    createLayout(coreEntity: Core.Entity, layoutModel: ModelLayout.Layout): Promise<ProjectionLayout.Entity>;

    createLayout(coreEntity: Core.Entity, layoutModel: ModelLayout.Layout, displayDictionary: ModelDictionary.DisplayDictionary, culture: string): Promise<ProjectionLayout.Entity>;

    createLayoutGrid(coreEntity: Core.Entity, gridModel: ModelGrid.Grid): Promise<ProjectionLayoutGrid.Grid>;

    createLayoutGrid(coreEntities: Core.Entity[], gridModel: ModelGrid.Grid): Promise<ProjectionLayoutGrid.Grid>;

    createLayoutConflictScenario(layoutEntity: ProjectionLayout.Entity, scenario: Core.ConflictScenario): Promise<ConflictScenarioLayout>;
}

export class ProviderServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public createIntegration(coreEntity: Core.Entity): Promise<ProjectionIntegration.Entity>;
    public createIntegration(actionContext: Wellspring.ActionContext, coreEntity: Core.Entity): Promise<ProjectionIntegration.Entity>;
    public async createIntegration(...args: unknown[]): Promise<ProjectionIntegration.Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Core.Entity"}
            Zod.instanceof(Core.Entity)
        ]).check(args)) {
            const [ coreEntity ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createIntegration');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createIntegration', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("5bef3c679b3a1a664f1980fce134d943973a4312957ce9818b55045ca35efb9b");
        
                    if (coreEntity && coreEntity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (coreEntity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            coreEntity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ProjectionIntegration.Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createIntegration`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                coreEntity: JSON.parse(JSON.stringify(coreEntity))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createIntegration" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ProjectionIntegration.Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ProjectionIntegration.Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createIntegration`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntity: JSON.parse(JSON.stringify(coreEntity))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createIntegration`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntity: JSON.parse(JSON.stringify(coreEntity))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public createBasic(coreEntity: Core.Entity): Promise<ProjectionBasic.Entity>;
    public createBasic(actionContext: Wellspring.ActionContext, coreEntity: Core.Entity): Promise<ProjectionBasic.Entity>;
    public async createBasic(...args: unknown[]): Promise<ProjectionBasic.Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Core.Entity"}
            Zod.instanceof(Core.Entity)
        ]).check(args)) {
            const [ coreEntity ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createBasic');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createBasic', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("c47678ce516749aa9b845f718dd4565023153a9f0e3bc159cf063dbe3173fd31");
        
                    if (coreEntity && coreEntity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (coreEntity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            coreEntity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ProjectionBasic.Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createBasic`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                coreEntity: JSON.parse(JSON.stringify(coreEntity))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createBasic" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ProjectionBasic.Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ProjectionBasic.Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createBasic`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntity: JSON.parse(JSON.stringify(coreEntity))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createBasic`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntity: JSON.parse(JSON.stringify(coreEntity))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public createLayout(coreEntity: Core.Entity): Promise<ProjectionLayout.Entity>;
    public createLayout(actionContext: Wellspring.ActionContext, coreEntity: Core.Entity): Promise<ProjectionLayout.Entity>;
    public createLayout(coreEntity: Core.Entity, layoutModel: ModelLayout.Layout): Promise<ProjectionLayout.Entity>;
    public createLayout(actionContext: Wellspring.ActionContext, coreEntity: Core.Entity, layoutModel: ModelLayout.Layout): Promise<ProjectionLayout.Entity>;
    public createLayout(coreEntity: Core.Entity, layoutModel: ModelLayout.Layout, displayDictionary: ModelDictionary.DisplayDictionary, culture: string): Promise<ProjectionLayout.Entity>;
    public createLayout(actionContext: Wellspring.ActionContext, coreEntity: Core.Entity, layoutModel: ModelLayout.Layout, displayDictionary: ModelDictionary.DisplayDictionary, culture: string): Promise<ProjectionLayout.Entity>;
    public async createLayout(...args: unknown[]): Promise<ProjectionLayout.Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Core.Entity"}
            Zod.instanceof(Core.Entity)
        ]).check(args)) {
            const [ coreEntity ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createLayout');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createLayout', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("891a8e9fa563706407c9a33133ece7c90cea35cb4b51b0068c7793fee561115d");
        
                    if (coreEntity && coreEntity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (coreEntity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            coreEntity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ProjectionLayout.Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createLayout`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                coreEntity: JSON.parse(JSON.stringify(coreEntity))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createLayout" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ProjectionLayout.Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ProjectionLayout.Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createLayout`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntity: JSON.parse(JSON.stringify(coreEntity))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createLayout`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntity: JSON.parse(JSON.stringify(coreEntity))
                    },
                    error
                });
        
                throw error;
            });
        }
        else if (Zod.tuple([
            // {"template":"model","className":"Core.Entity"}
            Zod.instanceof(Core.Entity), 
            // {"template":"model","className":"ModelLayout.Layout"}
            Zod.instanceof(ModelLayout.Layout)
        ]).check(args)) {
            const [ coreEntity, layoutModel ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createLayout');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createLayout', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(3);
                    writer.writeStringValue("a7bf66d1b1843e9b9df314345e65d3de9dd9afe53ac5cfc3d9833fab1a876fec");
        
                    if (coreEntity && coreEntity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (coreEntity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            coreEntity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (layoutModel && layoutModel !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (layoutModel === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            layoutModel.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ProjectionLayout.Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createLayout`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                coreEntity: JSON.parse(JSON.stringify(coreEntity)),
                                layoutModel: JSON.parse(JSON.stringify(layoutModel))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createLayout" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ProjectionLayout.Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ProjectionLayout.Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createLayout`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntity: JSON.parse(JSON.stringify(coreEntity)),
                        layoutModel: JSON.parse(JSON.stringify(layoutModel))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createLayout`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntity: JSON.parse(JSON.stringify(coreEntity)),
                        layoutModel: JSON.parse(JSON.stringify(layoutModel))
                    },
                    error
                });
        
                throw error;
            });
        }
        else if (Zod.tuple([
            // {"template":"model","className":"Core.Entity"}
            Zod.instanceof(Core.Entity), 
            // {"template":"model","className":"ModelLayout.Layout"}
            Zod.instanceof(ModelLayout.Layout), 
            // {"template":"model","className":"ModelDictionary.DisplayDictionary"}
            Zod.instanceof(ModelDictionary.DisplayDictionary), 
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ coreEntity, layoutModel, displayDictionary, culture ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createLayout');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createLayout', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(4);
                    writer.writeStringValue("201ffd6667b1de777daa71d4db818a8890c2c29ccc1db1bb9a4f4dda50559dd5");
        
                    if (coreEntity && coreEntity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (coreEntity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            coreEntity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (layoutModel && layoutModel !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (layoutModel === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            layoutModel.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (displayDictionary && displayDictionary !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (displayDictionary === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            displayDictionary.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (culture && culture !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (culture === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(culture);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ProjectionLayout.Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createLayout`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                coreEntity: JSON.parse(JSON.stringify(coreEntity)),
                                layoutModel: JSON.parse(JSON.stringify(layoutModel)),
                                displayDictionary: JSON.parse(JSON.stringify(displayDictionary)),
                                culture: JSON.parse(JSON.stringify(culture))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createLayout" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ProjectionLayout.Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ProjectionLayout.Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createLayout`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntity: JSON.parse(JSON.stringify(coreEntity)),
                        layoutModel: JSON.parse(JSON.stringify(layoutModel)),
                        displayDictionary: JSON.parse(JSON.stringify(displayDictionary)),
                        culture: JSON.parse(JSON.stringify(culture))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createLayout`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntity: JSON.parse(JSON.stringify(coreEntity)),
                        layoutModel: JSON.parse(JSON.stringify(layoutModel)),
                        displayDictionary: JSON.parse(JSON.stringify(displayDictionary)),
                        culture: JSON.parse(JSON.stringify(culture))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public createLayoutGrid(coreEntity: Core.Entity, gridModel: ModelGrid.Grid): Promise<ProjectionLayoutGrid.Grid>;
    public createLayoutGrid(actionContext: Wellspring.ActionContext, coreEntity: Core.Entity, gridModel: ModelGrid.Grid): Promise<ProjectionLayoutGrid.Grid>;
    public createLayoutGrid(coreEntities: Core.Entity[], gridModel: ModelGrid.Grid): Promise<ProjectionLayoutGrid.Grid>;
    public createLayoutGrid(actionContext: Wellspring.ActionContext, coreEntities: Core.Entity[], gridModel: ModelGrid.Grid): Promise<ProjectionLayoutGrid.Grid>;
    public async createLayoutGrid(...args: unknown[]): Promise<ProjectionLayoutGrid.Grid> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Core.Entity"}
            Zod.instanceof(Core.Entity), 
            // {"template":"model","className":"ModelGrid.Grid"}
            Zod.instanceof(ModelGrid.Grid)
        ]).check(args)) {
            const [ coreEntity, gridModel ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createLayoutGrid');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createLayoutGrid', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(5);
                    writer.writeStringValue("ef24feffdff9714de90656ff9606fdc0a68eb4aff4329721ffe3ada31e915148");
        
                    if (coreEntity && coreEntity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (coreEntity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            coreEntity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (gridModel && gridModel !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (gridModel === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            gridModel.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ProjectionLayoutGrid.Grid>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createLayoutGrid`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                coreEntity: JSON.parse(JSON.stringify(coreEntity)),
                                gridModel: JSON.parse(JSON.stringify(gridModel))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createLayoutGrid" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ProjectionLayoutGrid.Grid;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ProjectionLayoutGrid.Grid();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createLayoutGrid`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntity: JSON.parse(JSON.stringify(coreEntity)),
                        gridModel: JSON.parse(JSON.stringify(gridModel))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createLayoutGrid`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntity: JSON.parse(JSON.stringify(coreEntity)),
                        gridModel: JSON.parse(JSON.stringify(gridModel))
                    },
                    error
                });
        
                throw error;
            });
        }
        else if (Zod.tuple([
            // {"template":"array","itemImplementation":{"template":"model","className":"Core.Entity"}}
            Zod.array(Zod.instanceof(Core.Entity)), 
            // {"template":"model","className":"ModelGrid.Grid"}
            Zod.instanceof(ModelGrid.Grid)
        ]).check(args)) {
            const [ coreEntities, gridModel ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createLayoutGrid');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createLayoutGrid', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(6);
                    writer.writeStringValue("42c09fd01f967e2282f5662f6fe9b5ab00d69185b8c0bac52e61258ac734828e");
        
                    if (coreEntities && coreEntities !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (coreEntities === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(coreEntities.length);
                        
                            for (const item of coreEntities) {
                                // Item Implementation: model
                                if (item === null) {
                                    writer.writeByteValue(0);
                                }
                                else {
                                    writer.writeByteValue(1);
                                    item.slipstreamWrite(writer);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (gridModel && gridModel !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (gridModel === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            gridModel.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ProjectionLayoutGrid.Grid>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createLayoutGrid`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                coreEntities: JSON.parse(JSON.stringify(coreEntities)),
                                gridModel: JSON.parse(JSON.stringify(gridModel))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createLayoutGrid" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ProjectionLayoutGrid.Grid;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ProjectionLayoutGrid.Grid();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createLayoutGrid`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntities: JSON.parse(JSON.stringify(coreEntities)),
                        gridModel: JSON.parse(JSON.stringify(gridModel))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createLayoutGrid`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        coreEntities: JSON.parse(JSON.stringify(coreEntities)),
                        gridModel: JSON.parse(JSON.stringify(gridModel))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public createLayoutConflictScenario(layoutEntity: ProjectionLayout.Entity, scenario: Core.ConflictScenario): Promise<ConflictScenarioLayout>;
    public createLayoutConflictScenario(actionContext: Wellspring.ActionContext, layoutEntity: ProjectionLayout.Entity, scenario: Core.ConflictScenario): Promise<ConflictScenarioLayout>;
    public async createLayoutConflictScenario(...args: unknown[]): Promise<ConflictScenarioLayout> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"ProjectionLayout.Entity"}
            Zod.instanceof(ProjectionLayout.Entity), 
            // {"template":"model","className":"Core.ConflictScenario"}
            Zod.instanceof(Core.ConflictScenario)
        ]).check(args)) {
            const [ layoutEntity, scenario ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createLayoutConflictScenario');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createLayoutConflictScenario', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(7);
                    writer.writeStringValue("9aac3cdee096f30751d4d0ef5b3864d6ac3dfe808ba92d0cdbd60921a73c8b0d");
        
                    if (layoutEntity && layoutEntity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (layoutEntity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            layoutEntity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (scenario && scenario !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (scenario === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            scenario.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ConflictScenarioLayout>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createLayoutConflictScenario`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                layoutEntity: JSON.parse(JSON.stringify(layoutEntity)),
                                scenario: JSON.parse(JSON.stringify(scenario))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createLayoutConflictScenario" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ConflictScenarioLayout;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ConflictScenarioLayout();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createLayoutConflictScenario`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        layoutEntity: JSON.parse(JSON.stringify(layoutEntity)),
                        scenario: JSON.parse(JSON.stringify(scenario))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createLayoutConflictScenario`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        layoutEntity: JSON.parse(JSON.stringify(layoutEntity)),
                        scenario: JSON.parse(JSON.stringify(scenario))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'ConflictScenarioLayout':
                return new ConflictScenarioLayout();
            case 'ConflictScenarioLayoutQuestion':
                return new ConflictScenarioLayoutQuestion();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'ConflictScenarioLayout':
                return new ConflictScenarioLayout(source);
            case 'ConflictScenarioLayoutQuestion':
                return new ConflictScenarioLayoutQuestion(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'ConflictScenarioLayout':
                return ConflictScenarioLayout.parse(source);
            case 'ConflictScenarioLayoutQuestion':
                return ConflictScenarioLayoutQuestion.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
